import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowLeft, CalendarCheck, MapPin, Clock, Loader2, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS = {
  requested: { label: "Requested", cls: "bg-brand-accent/15 text-brand-accent" },
  contacted: { label: "Clinic contacted you", cls: "bg-brand-primary/10 text-brand-primary" },
  booked: { label: "Consult booked", cls: "bg-brand-primary/10 text-brand-primary" },
  closed: { label: "Closed", cls: "bg-brand-sand text-brand-slate" },
};

export default function MyConsults() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [consults, setConsults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    api.get("/consults").then((r) => setConsults(r.data.consults || [])).catch(() => {}).finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  return (
    <div className="min-h-screen bg-brand-cream text-brand-ink">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
            <span className="font-heading text-xl">AuraLift</span>
          </Link>
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-brand-slate hover:text-brand-primary" data-testid="consults-back-btn">
            <ArrowLeft className="w-4 h-4" /> Studio
          </button>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-heading text-4xl sm:text-5xl mb-2">My consults</h1>
        <p className="text-brand-slate mb-8">Every consult you've requested and where it's at.</p>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-brand-primary animate-spin" /></div>
        ) : consults.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-brand-border/60" data-testid="consults-empty">
            <Stethoscope className="w-10 h-10 text-brand-slate/40 mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-heading text-xl mb-1">No consults yet</p>
            <p className="text-sm text-brand-slate mb-6">Find a clinic and request a consult to get started.</p>
            <Button onClick={() => navigate("/clinics")} className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white">Browse clinics</Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="consults-list">
            {consults.map((c, i) => {
              const st = STATUS[c.status] || STATUS.requested;
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}
                  data-testid={`consult-item-${c.id}`}
                  className="bg-white rounded-2xl border border-brand-border/60 p-5 flex items-start justify-between gap-4"
                >
                  <div className="min-w-0">
                    <p className="font-heading text-lg leading-tight">{c.clinic_name}</p>
                    <p className="text-sm text-brand-slate mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="flex items-center gap-1"><CalendarCheck className="w-3.5 h-3.5" /> {c.procedure || "General consultation"}</span>
                      {c.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {c.location}</span>}
                    </p>
                    <p className="text-xs text-brand-slate mt-2 flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(c.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium shrink-0 ${st.cls}`}>{st.label}</span>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
